<template>
    <select v-model="selectedKey" :disabled="readonly">
        <option v-if="addSelect" value="">&lt;--{{ prefix }} <slot></slot> {{ suffix }}--&gt;</option>
        <option v-for="option in sortedList" :value="option.Key" :selected="isMatch(option.Key)" >{{ option.Value }}</option>
    </select>
</template>

// 

<script lang="ts">

import { defineComponent } from "vue";

export interface SelectionEntry {
    Key: number|string;
    Value: string;
}

/**
 * Make a selection list for use with <selection> components
 * @param enumType An enumeration type.
 * @param enumTypeDescription An array of displayable strings, each matching an enum value. These are typically defined where the enum is defined.
 * @returns An array of options that can be used by a <selection> component.
 * @example const options = EnumSelections(GenericActiveStatus, GenericActiveStatusDescription);
 */
// eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
export function EnumSelections(enumType: any, enumTypeDescription: string[]): SelectionEntry[] {
    const options: SelectionEntry[] = [];
    for (const e in enumType) {
        const key = Number(e);
        if (!isNaN(key)) {
            options.push({ Key: key, Value: enumTypeDescription[key]});
        }
    }
    return options;
}

const SelectionComponent = defineComponent({
    props: {
        options: { type: Array as () => SelectionEntry[], required: false, default: () => [] },
        modelValue: { type: [String, Number], required: true, default: "" },
        readonly: { type: Boolean, required: false, default: false },
        required: { type: Boolean, required: false, default: false },
        sort: { type: String, required: false, default: "none" },
        addSelect: { type: Boolean, required: false, default: true },
    },
    emits: ["update:modelValue"],
    computed: {
        selectedKey: {
            get(): string | number {
                return this.modelValue;
            },
            set(key: string | number): void {
                this.$emit("update:modelValue", key);
            },
        },
        prefix(): string {
            return this.required ? "" : " Select";
        },
        suffix(): string {
            return this.required ? "Required " : "";
        },
        sortedList(): SelectionEntry[] {
            const arr: SelectionEntry[] = [];
            let sortedArray = arr;
            if (this.options && this.sort) {
                for(const entry of this.options) {
                    arr.push(entry);
                }
                sortedArray = arr;
                if (this.sort === "Value") {
                    sortedArray = arr.sort((a, b): number => {
                        const x = a.Value.toLowerCase();
                        const y = b.Value.toLowerCase();
                        return x > y ? 1 : y > x ? -1 : 0;
                    });
                } else if (this.sort === "Key") {
                    sortedArray = arr.sort((a, b): number => {
                        const x = a.Key;
                        const y = b.Key;
                        return x > y ? 1 : y > x ? -1 : 0;
                    });
                }
            }
            return sortedArray;
        },
    },
    methods: {
        isMatch(key: string | number): boolean {
            // comparison below deliberatey with type coersion because key may be a string.
            // eslint-disable-next-line eqeqeq
            return key == this.selectedKey;
        },
    },
});

// Add static property for backwards compatibility
(SelectionComponent as any).EnumSelections = EnumSelections;

export default SelectionComponent;

</script>
